import { Box } from '@mui/material';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { ProjectException } from '../../../project-exceptions/models/ProjectException';
import { BlankCell } from '../../components/Cells/BlankCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, BackwardsCursor, CursorInfo, ForwardsCursor, PaginationResult } from '../../types/pagination';
import { StackFrame } from '../models/StackFrame';
import { CreateExceptionButton } from './CreateExceptionButton';

type State = PaginatedTableState & {};

type Props = {
  title: string;
  initialFrames: StackFrame[] | null;
  parentLoading: boolean;
  onCreateException?: (term: string) => Promise<ProjectException | null>;
};

export const DEFAULT_LOCATIONS_PAGE_SIZE = 10;

export class StackTraceTable extends PaginatedTableController<StackFrame, Props, State> {
  static defaultProps = {
    initialFrames: null,
  };

  protected isLoading() {
    return super.isLoading() || this.props.parentLoading;
  }

  protected getPrefilledConfig(): PrefilledDataConfig<StackFrame> {
    const { initialFrames, parentLoading } = this.props;
    if (!initialFrames) {
      return { prefillActive: true, initialLoading: parentLoading, initialData: null };
    }
    const pageSize = this.getDefaultPageSize();
    const pageData = initialFrames.slice(0, pageSize);
    return {
      prefillActive: true,
      initialLoading: parentLoading,
      initialData: {
        data: pageData,
        totalCount: initialFrames.length,
        pageInfo: {
          hasPreviousPage: false,
          hasNextPage: initialFrames.length > pageSize,
          startCursor: pageData[0].getId().toString(),
          endCursor: pageData[pageData.length - 1].getId().toString(),
        },
      },
    };
  }

  protected getDefaultPageSize(): number {
    return 5;
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: this.props.title };
  }

  protected getFieldConfigurations(): FieldConfiguration<StackFrame>[] {
    const { onCreateException } = this.props;

    const fields: FieldConfiguration<StackFrame>[] = [
      {
        fieldKey: 'method',
        label: t('method'),
        description: t('method'),
        render: (row, { hasFocus }) => {
          const content = row.getFullMethodName();
          if (!content) {
            return <BlankCell blankText={t('unknown')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{content}</TypographyOverflow>;
        },
      },
      {
        fieldKey: 'line',
        label: t('line'),
        description: t('line'),
        render: (row, { hasFocus }) => {
          const content = row.getFullFileName();
          if (!content) {
            return <BlankCell blankText={t('unknown')} />;
          }
          return <TypographyOverflow hasFocus={hasFocus}>{content}</TypographyOverflow>;
        },
      },
    ];
    if (onCreateException) {
      fields.push({
        fieldKey: 'createException',
        label: '',
        description: t('addException'),
        columnProps: {
          align: 'right',
          maxWidth: 275,
        },
        render: (row, { tabIndex }) => <CreateExceptionButton tabIndex={tabIndex} frame={row} onCreate={onCreateException} />,
      });
    }
    return fields;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected renderTable(tableComponent: ReactElement) {
    return <Box className="stacktrace-table">{tableComponent}</Box>;
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<StackFrame>> {
    const { initialFrames } = this.props;

    if (!initialFrames) {
      return BLANK_PAGINATION_RESULT;
    }

    const { after, first } = cursorInfo as ForwardsCursor;
    const { before, last } = cursorInfo as BackwardsCursor;
    let pageData: StackFrame[] = [];
    let hasNextPage = false;
    let hasPreviousPage = false;
    if (after || first) {
      const start = after ? Number(after) + 1 : 0;
      const end = start + first;
      pageData = initialFrames.slice(start, end);
      hasNextPage = end < initialFrames.length;
      hasPreviousPage = start > 0;
    }
    if (before || last) {
      const start = before ? Number(before) : initialFrames.length - 1;
      pageData = initialFrames.slice(Math.max(0, start - last), start);
      hasNextPage = true;
      hasPreviousPage = start - last > 0;
    }
    const startCursor = pageData[0].getId().toString();
    const endCursor = pageData[pageData.length - 1].getId().toString();

    return {
      data: pageData,
      pageInfo: { hasNextPage, hasPreviousPage, startCursor, endCursor },
      totalCount: initialFrames.length,
    };
  }
}
